import PropTypes from 'prop-types';
import * as React from 'react';

import { Grid, AutoSizer, ColumnSizer } from 'react-virtualized'

import ScrollView from 'components/ScrollView';

import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import HipchatChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';

import SORT_TYPES from 'constants/sortTypes';

import commonStyle from 'commonStyle'

import './index.css';


export default class AutoSizerExample extends React.PureComponent{
  static defaultProps = {
    backgroundColour: 'transparent',
    data: [],
    selectionBackgroundColour: commonStyle.dimPrimary(0.1),
    selections: new Set([])    
  }

  constructor(props) {
    super(props);

    this.state = {
      columnMaxWidth: 800,
      columnMinWidth: 300,
      data: props.data,
      selections: props.selections,
      sortType: SORT_TYPES.DSC,
      sortColumn: -1
    };

    this._getSortIcon = this._getSortIcon.bind(this);
    this._noColumnMaxWidthChange = this._noColumnMaxWidthChange.bind(this);
    this._noColumnMinWidthChange = this._noColumnMinWidthChange.bind(this);
    this._onColumnCountChange = this._onColumnCountChange.bind(this);
    this._noContentRenderer = this._noContentRenderer.bind(this);
    this._cellRenderer = this._cellRenderer.bind(this);
    this._headerRenderer = this._headerRenderer.bind(this);
  }

  componentWillReceiveProps(props) {
    const newState = {};
    const { sortColumn, sortType } = this.state;
    const { columns = [] } = props;

    if (props.data) {
      newState.data = props.data;

      if (sortColumn !== -1) {
        const column = columns[sortColumn];

        if (column) {
          if (column.onSort) {
            newState.data = column.onSort(props.data, sortType)
          }
        }
      }
    }

    if (props.selections) {
      newState.selections = props.selections;
    }

    this.setState(newState);
  }

  render() {
    const {bodyHeight, columns, fetchMore, hideHeader = false} = this.props;

    const { columnMaxWidth, columnMinWidth, data, selections, sortColumn, sortType} = this.state;

    const columnCount = columns.length;
    const rowCount = data.length;
  
    return (
      <AutoSizer disableHeight>
        {({width}) => (
          <ColumnSizer
            columnMaxWidth={columnMaxWidth}
            columnMinWidth={columnMinWidth}
            columnCount={columnCount}
            key="GridColumnSizer"
            width={width}>
            {({adjustedWidth, columnWidth, registerChild}) => {
              return (
                <ScrollView
                  style={{
                    width: adjustedWidth
                  }}
                  gap={0.6}
                  onBottom={fetchMore}
                >
                  { !hideHeader &&
                    <Grid
                      className='table__header'
                      ref={registerChild}
                      columnWidth={columnWidth}
                      columnCount={columnCount}
                      height={40}
                      noContentRenderer={this._noContentRenderer}
                      cellRenderer={this._headerRenderer(sortType, sortColumn)}
                      rowHeight={40}
                      rowCount={1}
                      width={adjustedWidth}
                    />
                  }
                  <Grid
                    className='table__body'
                    ref={registerChild}
                    columnWidth={columnWidth}
                    columnCount={columnCount}
                    height={bodyHeight || 500}
                    noContentRenderer={this._noContentRenderer}
                    cellRenderer={this._cellRenderer(selections)}
                    rowHeight={50}
                    rowCount={rowCount}
                    width={adjustedWidth}
                  />
                </ScrollView>
              )
            }}
          </ColumnSizer>
        )}
      </AutoSizer>
    )
  }

  _noColumnMaxWidthChange(event) {
    let columnMaxWidth = parseInt(event.target.value, 10);

    if (isNaN(columnMaxWidth)) {
      columnMaxWidth = undefined;
    } else {
      columnMaxWidth = Math.min(1000, columnMaxWidth);
    }

    this.setState({columnMaxWidth});
  }

  _noColumnMinWidthChange(event) {
    let columnMinWidth = parseInt(event.target.value, 10);

    if (isNaN(columnMinWidth)) {
      columnMinWidth = undefined;
    } else {
      columnMinWidth = Math.max(1, columnMinWidth);
    }

    this.setState({columnMinWidth});
  }

  _onColumnCountChange(event) {
    this.setState({columnCount: parseInt(event.target.value, 10) || 0});
  }

  _noContentRenderer() {
    return (
      <div className='table__empty'>
        <img className='table__empty__icon' src={require('assets/midrive_icon.png')}/>
        <p className='table__empty__title'>No cells found. You can drag pictures, videos and folders into your dashboard.</p>
      </div>
    )
  }

  _getSortIcon(type) {
    switch(type) {
      case SORT_TYPES.ASC:
        return (<HipchatChevronUpIcon />)
      case SORT_TYPES.DSC:
        return (<HipchatChevronDownIcon />)
    }
  }

  _headerRenderer(sortType, sortColumn) {
    return ({columnIndex, key, style}) => {
      const { columns } = this.props;

      const column = columns[columnIndex];

      const onSort = column.onSort;

      return (
        <div
          className={`table__header__cell${sortColumn === columnIndex ?' table__header__cell--border' : ''}`}
          key={key}
          style={style}
          onClick={() => {
            let sortType = SORT_TYPES.DSC;

            if (this.state.sortType === SORT_TYPES.DSC) {
              sortType = SORT_TYPES.ASC
            }

            if (onSort) {
              this.setState({
                data: onSort(this.state.data, sortType),
                sortType,
                sortColumn: columnIndex
              })
            }
          }}
        >
          <h4>{column.Header}</h4>
          {onSort && this._getSortIcon(sortType)}
        </div>
      )
    }
  }

  _cellRenderer(selections) { 
    return ({columnIndex, key, rowIndex, style} = {}) => {
      const {
        columns,
        getTrProps
      } = this.props;

      const {
        data
      } = this.state;

      let trStyle, onClick, trProps;

      const row = data[rowIndex]

      const column = columns[columnIndex];

      let value = ''

      if (typeof column.accessor === 'function') {
        value = column.accessor(row);
      } else {
        value = row[column.accessor]
      }
      const _props = {
        original: row,
        value
      }

      const className = 'table__body__cell';

      if (getTrProps) {
        const result = getTrProps(null, _props);

        if (result) {
          trStyle = result.style;
          onClick = result.onClick;
          trProps = result.trProps || {}
        }
      }

      return (
        <div
          className={className}
          key={key}
          style={style}
          onClick={e => {
            if(onClick && typeof onClick === 'function') {
              onClick(e, {
                isSelected: selections.has(row.id)
              })
            }
          }}
          {...trProps}
        >
          {column.Cell(_props)}
        </div>
      );
    }
  }
}